import React, { useEffect, useMemo, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import frFR from "antd/locale/fr_FR";
import { Route, Routes } from "react-router-dom";
import { buildTheme } from "./theme";
import { useThemeMode } from "./hooks/useThemeMode";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import AppLayout from "./layout/AppLayout";
import HomePage from "./pages/HomePage";
import DashboardPage from "./pages/DashboardPage";
import StudentsListPage from "./pages/StudentsListPage";
import StudentFormPage from "./pages/StudentFormPage";
import StudentImportPage from "./pages/StudentImportPage";
import StudentDetailPage from "./pages/StudentDetailPage";
import ReferentialsPage from "./pages/ReferentialsPage";
import PedagogyPage from "./pages/PedagogyPage";
import PedagogyAdminPage from "./pages/PedagogyAdminPage";
import SchoolPage from "./pages/SchoolPage";
import PaymentsPage from "./pages/PaymentsPage";
import PersonnelPage from "./pages/PersonnelPage";
import AdministrationPage from "./pages/AdministrationPage";
import EngagementPage from "./pages/EngagementPage";
import ReferencesPage from "./pages/ReferencesPage";
import CertificatePrintPage from "./pages/CertificatePrintPage";
import LoginPage from "./pages/LoginPage";
import CurriculumOnboardingPage from "./pages/CurriculumOnboardingPage";
import CurriculumGuard from "./routes/CurriculumGuard";

/**
 * usePrefersReducedMotion — suit la préférence système, y compris si elle
 * change en cours de session (réglage modifié sans recharger la page).
 */
const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

const App: React.FC = () => {
  const reducedMotion = usePrefersReducedMotion();
  const { mode, dark, setMode } = useThemeMode();

  // `motion: false` coupe l'animation par le mécanisme d'antd lui-même, au
  // lieu de la neutraliser en CSS : rc-motion sait alors qu'il n'a aucun
  // événement à attendre, et les menus déroulants continuent de s'ouvrir
  // (cf. la note dans src/index.css).
  //
  // L'algorithme sombre d'antd recalcule TOUS les jetons dérivés (fonds,
  // bordures, textes) à partir des mêmes couleurs de marque : la charte est
  // donc préservée, seule la luminance change.
  const activeTheme = useMemo(() => {
    const base = buildTheme(dark);
    return {
      ...base,
      algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: { ...base.token, motion: !reducedMotion },
    };
  }, [reducedMotion, dark]);

  return (
    <ConfigProvider theme={activeTheme} locale={frFR}>
      {/* AuthProvider est DANS le Router : il consomme useApiClient, qui
          utilise useNavigate. Le placer plus haut lèverait une exception. */}
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Onboarding « programme marocain » : plein écran, hors coquille. */}
          <Route
            path="/programme"
            element={
              <ProtectedRoute>
                <CurriculumOnboardingPage />
              </ProtectedRoute>
            }
          />
          {/* Tout ce qui vit dans la coquille exige une session vérifiée.
              Auparavant l'application s'affichait sans contrôle et comptait sur
              un 401 d'une requête de données pour rediriger : sans réponse du
              serveur (réseau, CORS), personne ne redirigeait et l'utilisateur
              restait sur une liste vide. */}
          <Route
            element={
              <ProtectedRoute>
                {/* Direction d'un établissement qui n'a pas encore choisi son
                    programme → /programme. Sans effet pour les autres comptes. */}
                <CurriculumGuard>
                  {/* Le thème est piloté ici (ConfigProvider) : la coquille reçoit
                      l'état et son sélecteur plutôt que de tenir un second état,
                      qui divergerait de celui appliqué. */}
                  <AppLayout themeMode={mode} onThemeModeChange={setMode} />
                </CurriculumGuard>
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/eleves" element={<StudentsListPage />} />
            <Route path="/students/new" element={<StudentFormPage />} />
            <Route path="/students/import" element={<StudentImportPage />} />
            <Route path="/students/:id" element={<StudentDetailPage />} />
            <Route path="/referentiels" element={<ReferentialsPage />} />
            <Route path="/pedagogie" element={<PedagogyPage />} />
            <Route path="/admin-pedagogique" element={<PedagogyAdminPage />} />
            <Route path="/ecole" element={<SchoolPage />} />
            <Route path="/paiements" element={<PaymentsPage />} />
            <Route path="/personnel" element={<PersonnelPage />} />
            <Route path="/administration" element={<AdministrationPage />} />
            <Route path="/engagement" element={<EngagementPage />} />
            <Route path="/references" element={<ReferencesPage />} />
            <Route path="/enrollments/:id/print" element={<CertificatePrintPage />} />
          </Route>
          {/* /status : page d'état du service, volontairement publique. */}
          <Route path="/status" element={<HomePage />} />
        </Routes>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
